/**
 * O convite de instalação do painel, do lado do navegador.
 *
 * Mora em `lib/` e é importado pelo `main.tsx` de propósito: o
 * `beforeinstallprompt` dispara UMA vez, cedo, e o Chrome não o repete. Na
 * primeira versão disto o listener vivia no componente do convite — que o
 * Vite empacota no chunk da rota `_authed`, carregado depois do entry. Se o
 * evento chegasse nessa janela, ele caía no vazio, e o botão "Instalar"
 * aparecia sem nada por trás.
 *
 * Aqui o listener registra junto do bundle de entrada, antes de qualquer
 * rota, que é o mais cedo que dá.
 */

/** O evento do Chrome. Não está no lib.dom, então descrevemos o que usamos. */
type PromptEvent = Event & {
  prompt: () => Promise<{ outcome: "accepted" | "dismissed" }>;
};

let guardado: PromptEvent | null = null;
const inscritos = new Set<() => void>();

function avisar(): void {
  for (const notificar of inscritos) notificar();
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    // Sem isto o Chrome desenha a própria barra de instalação, e ficariam
    // dois convites na tela dizendo a mesma coisa.
    e.preventDefault();
    guardado = e as PromptEvent;
    avisar();
  });

  // Instalou (pelo nosso botão ou pelo menu do navegador): o convite não tem
  // mais o que sugerir.
  window.addEventListener("appinstalled", () => {
    guardado = null;
    avisar();
  });
}

/** Há um prompt nativo pronto pra ser disparado? */
export function temPromptNativo(): boolean {
  return guardado !== null;
}

/** Avisa quando a resposta de `temPromptNativo` muda. Devolve o cancelamento. */
export function aoMudarPrompt(notificar: () => void): () => void {
  inscritos.add(notificar);
  return () => {
    inscritos.delete(notificar);
  };
}

export type ResultadoInstalacao = "aceito" | "recusado" | "indisponivel";

/**
 * Dispara o diálogo nativo. Precisa ser chamado DENTRO do gesto do usuário —
 * o Chrome recusa um `prompt()` solto.
 *
 * Nunca lança: quem chama precisa poder mostrar o caminho manual quando o
 * nativo não rolar, e uma exceção aqui viraria promessa rejeitada sem dono —
 * que foi como o botão passou a não fazer nada.
 */
export async function dispararInstalacao(): Promise<ResultadoInstalacao> {
  const evento = guardado;
  if (!evento) return "indisponivel";
  try {
    const { outcome } = await evento.prompt();
    // O evento é de uso único: aceito ou recusado, ele não serve mais.
    guardado = null;
    avisar();
    return outcome === "accepted" ? "aceito" : "recusado";
  } catch {
    // Estourou o gesto, o evento venceu, ou o navegador mudou de ideia.
    // Devolver o cartucho não ajuda — o caminho manual, sim.
    guardado = null;
    avisar();
    return "indisponivel";
  }
}
